/** Gemeinsame Typen und Hilfen für den Portal-Dokumente-Hub. */

import {
  PORTAL_DOCUMENT_FOLDER_URL,
  PORTAL_PROTOKOLLE_COLLECTION,
  getPortalPocketBaseUrl,
  getPortalPbToken,
  getPortalTenantN8nExtras,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  portalAuthzSidecarExpected,
  resolveOnlyofficeIssueJwtUrl,
} from '../../constants';
import { formatPortalUploadError } from './portalErrorMessages';
import {
  applyPortalDocumentRenameToShortcuts,
} from './portalDocumentState';
import { mapPbOptionalStringId } from './portalDocumentsPrivacy';

export {
  buildPortalDocumentsPbFilter,
  portalSeatRootNcPath,
  portalDocumentRelativeFolder,
  portalCustomerFacingRelativeFolder,
  portalFolderRelativeFromNcPath,
} from './portalDocumentsPrivacy';

export interface PortalProtokoll {
  id: string;
  filename: string;
  nc_url: string;
  nextcloud_path?: string;
  created: string;
  updated?: string;
  last_revision_id?: number;
  last_save_callback_at?: string;
  /** Bytes aus PB/NC wenn verfügbar */
  file_size?: number;
  mime_type?: string;
  /**
   * PB `portal_documents.akte_id` — optional, nur echte Relation-Id.
   * Leer/fehlend = unzugeordnet (kein Pfad-Rate im Mapper).
   */
  akte_id?: string;
  /** PB `portal_documents.customer_id` — Owner-Sitz, nicht aus dem NC-Pfad geraten. */
  customer_id?: string;
  /** PB `portal_documents.folder_id` — optional, nur echte Id. */
  folder_id?: string;
}

export interface PortalRecentDocument {
  display_name: string;
  pb_record_id?: string;
  nextcloud_path?: string;
  document_url: string;
  openedAt: string;
}

/** Slice 1.3: Offene Editor-Tabs — Metadaten für ARKO-Webhook (ohne Volltext). */
export interface PortalOpenDocumentMeta {
  documentId: string;
  title: string;
  nc_path: string | null;
  /** true = aktuell fokussiertes Dokument */
  active?: boolean;
}

/** Slice 1.3b: Gewählte Docs für Synthese — voller Kontext/Extract pro Doc. */
export interface PortalSelectedDocumentPayload {
  documentId: string;
  title: string;
  nc_path: string | null;
  documentContext: string;
  documentExtractFingerprint?: string;
  /** Ungekappter Extract-Cache für Hintergrundanalyse (Chat bleibt am 7500-Fenster). */
  fullExtractText?: string;
  extractPages?: number;
  extractRawChars?: number;
  extractTruncated?: boolean;
  extractSource?: string;
}

/** Max. gleichzeitig geöffnete OnlyOffice-Editor-Tabs (reine UI-Liste; nur ein Embed aktiv). */
export const PORTAL_MAX_OPEN_DOCUMENTS = 10;

/** Max. gleichzeitig aktive ARKO-Chat-Tabs (Workspace localStorage). */
export const PORTAL_MAX_OPEN_CHATS = 10;

/** Max. gleichzeitig selektierbare Docs (Token-Budget V1). */
export const PORTAL_SYNTHESIS_MAX_SELECTED_DOCS = 5;

export function formatSynthesisDocumentLabels(titles: string[]): string | null {
  const clean = titles.map((t) => t.trim()).filter(Boolean);
  if (clean.length < 2) return null;
  return clean.join(' + ');
}

export {
  applyPortalDocumentRenameToProtokolle,
  applyPortalDocumentRenameToShortcuts,
  buildMetadataOnlySynthesisContext,
  portalSelectedDocumentFromMeta,
} from './portalDocumentState';

/** Aktenraum: Inventar + Kurz-Stand — Logik in `portalAkteStand.ts` (24B, kein Voll-PDF). */
export {
  PORTAL_AKTE_INVENTORY_MAX_DOCS,
  PORTAL_AKTE_STAND_MAX_CHARS,
  PORTAL_AKTE_STAND_ORIENTATION_SNIPPET,
  PORTAL_AKTE_STAND_MAX_DOC_TITLES,
  PORTAL_AKTE_FOLLOWUP_CONTEXT_MARKER,
  collapseAkteStandText,
  buildAkteThreadStand,
  formatAkteStandParagraph,
  previewAkteStandLabel,
  akteStandContentEquals,
  appendAkteStandToDocumentContext,
  akteSelectedDocumentsForTurn,
  buildAkteTurnContextPayload,
  buildAkteInventoryDocumentContext,
  boundAkteFocusExtractIfSelected,
  aktePromptHasExtractBody,
  PORTAL_AKTE_SELECTED_EXTRACTS_MAX,
  PORTAL_AKTE_NO_FULL_PDF_HINT,
} from './portalAkteStand';
export type { PortalAkteThreadStand, AkteSelectedExtract } from './portalAkteStand';

/** Zuletzt bewusst geöffnete Dokumente im Schnellzugriff (Explorer-UAT 23.08.2026). */
export const PORTAL_RECENT_DOCS_MAX = 5;

export const PORTAL_DOCUMENT_FILE_ACCEPT =
  '.pdf,.jpg,.jpeg,.png,.doc,.docx,.odt,.rtf,.txt,.md,.markdown,.html,.htm,.csv,.xls,.xlsx,.ods,.ppt,.pptx,.odp,audio/*,.m4a,.mp3,.wav,.ogg,.webm,.aac,.opus,application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/markdown,text/plain,text/html,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';

const AUDIO_EXT = new Set(['m4a', 'mp3', 'wav', 'ogg', 'webm', 'aac', 'opus', 'flac']);

export function fileExtensionFromName(name: string): string {
  const m = name.trim().toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
}

export {
  ensurePortalDocxFileName,
  fileTypeFromMime,
  isPortalImageFile,
  isLikelyBlankPortalDocumentName,
  portalImageUploadPolicyError,
  PORTAL_IMAGE_OCR_NOTICE,
  PORTAL_LOCAL_OCR_MARKER,
  portalDocumentBasename,
  resolvePortalExtractFileType,
  resolvePortalDocumentFileType,
} from './portalFileType';

/** Anzeige für Explorer-Spalte Größe. */
export function formatPortalFileSize(bytes?: number | null): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function portalDocumentTypeLabel(
  filename: string,
  mimeType?: string | null,
): string {
  const mime = mimeType?.trim().toLowerCase();
  if (mime) {
    if (mime.includes('pdf')) return 'PDF';
    if (mime.startsWith('image/')) return 'Bild';
    if (mime.includes('word') || mime.includes('document')) return 'Word';
    if (mime.includes('spreadsheet') || mime.includes('excel')) return 'Tabelle';
    if (mime.includes('presentation')) return 'Präsentation';
    if (mime.startsWith('audio/')) return 'Audio';
    if (mime.startsWith('text/')) return 'Text';
  }
  const ext = fileExtensionFromName(filename);
  if (ext === 'jpg' || ext === 'jpeg' || ext === 'png') return 'Bild';
  if (!ext) return 'Datei';
  return ext.toUpperCase();
}

export function isAudioFile(file: File): boolean {
  if (file.type.startsWith('audio/')) return true;
  const ext = fileExtensionFromName(file.name);
  return AUDIO_EXT.has(ext);
}

export function isPortalPlainTextExtension(ext: string): boolean {
  return ext === 'txt' || ext === 'md' || ext === 'markdown' || ext === 'csv';
}

export function isPortalDocxForEditor(filename: string, nextcloudPath?: string): boolean {
  const ext =
    fileExtensionFromName(filename) || fileExtensionFromName(nextcloudPath || '');
  return ext === 'docx' || ext === 'doc';
}

export function canOpenDocumentInPortal(
  isPremium: boolean,
  filename: string,
  nextcloudPath: string | undefined,
  documentUrl: string | undefined,
): boolean {
  if (!isPremium || !documentUrl) return false;
  const ext =
    fileExtensionFromName(filename) ||
    fileExtensionFromName(nextcloudPath || '') ||
    fileExtensionFromName(documentUrl);
  return (
    isPortalDocxForEditor(filename, nextcloudPath) ||
    isPortalPlainTextExtension(ext) ||
    isPortalPdfDocument(filename, nextcloudPath, documentUrl)
  );
}

/** PB `portal_documents`: aktive Zeilen fuer Hub-Liste (ohne archivierte). */
export function isActivePortalDocumentRecord(record: Record<string, unknown>): boolean {
  const st = record.status;
  if (st === 'archived') return false;
  return st === 'active' || st === 'draft' || st == null || st === '';
}

/** PB list sort — `-created`/`-updated` → HTTP 400 auf pb-labor/pb-thomas (vgl. memcore.ts, lessons 12.06.2026). */
export const PORTAL_DOCUMENTS_PB_LIST_SORT = '-id';
export const PORTAL_DOCUMENTS_PB_LIST_PER_PAGE = 100;
export const PORTAL_DOCUMENTS_PB_LIST_MAX_PAGES = 20;

/** `-id` ist nicht chronologisch: Seite 1 kann eine frische GET-Zeile weglassen. */
export function shouldFetchNextPortalDocumentPage(input: {
  page: number;
  received: number;
  perPage?: number;
  totalItems?: number;
  totalPages?: number;
}): boolean {
  const perPage = input.perPage ?? PORTAL_DOCUMENTS_PB_LIST_PER_PAGE;
  if (input.page < 1 || input.received <= 0) return false;
  if (input.page >= PORTAL_DOCUMENTS_PB_LIST_MAX_PAGES) return false;
  if (input.received < perPage) return false;
  if (typeof input.totalPages === 'number' && input.page >= input.totalPages) return false;
  if (typeof input.totalItems === 'number' && input.page * perPage >= input.totalItems) return false;
  return true;
}

export function mapPbRecordToProtokoll(r: Record<string, unknown>): PortalProtokoll {
  const id = typeof r.id === 'string' ? r.id : String(r.id ?? '');
  const ncUrl =
    (typeof r.document_url === 'string' && r.document_url.trim()) ||
    (typeof r.nc_url === 'string' && r.nc_url.trim()) ||
    '';
  const filename =
    (typeof r.display_name === 'string' && r.display_name.trim()) ||
    (typeof r.filename === 'string' && r.filename.trim()) ||
    'Dokument';
  const created = typeof r.created === 'string' ? r.created : new Date().toISOString();
  const nextcloud_path =
    typeof r.nextcloud_path === 'string' && r.nextcloud_path.trim()
      ? r.nextcloud_path.trim()
      : undefined;
  const updated = typeof r.updated === 'string' ? r.updated : undefined;
  const revRaw = r.last_revision_id;
  const revNum = revRaw != null ? Number(revRaw) : NaN;
  const last_revision_id =
    Number.isFinite(revNum) && revNum >= 0 ? Math.floor(revNum) : undefined;
  const last_save_callback_at =
    typeof r.last_save_callback_at === 'string' && r.last_save_callback_at.trim()
      ? r.last_save_callback_at.trim()
      : undefined;
  const sizeRaw = r.file_size ?? r.size ?? r.filesize;
  const sizeNum = sizeRaw != null ? Number(sizeRaw) : NaN;
  const file_size = Number.isFinite(sizeNum) && sizeNum >= 0 ? Math.floor(sizeNum) : undefined;
  const mime_type =
    typeof r.mime_type === 'string' && r.mime_type.trim()
      ? r.mime_type.trim()
      : typeof r.content_type === 'string' && r.content_type.trim()
        ? r.content_type.trim()
        : undefined;
  const akte_id =
    typeof r.akte_id === 'string' && r.akte_id.trim() ? r.akte_id.trim() : undefined;
  const customer_id = mapPbOptionalStringId(r, 'customer_id');
  const folder_id = mapPbOptionalStringId(r, 'folder_id');
  return {
    id,
    filename,
    nc_url: ncUrl,
    nextcloud_path,
    created,
    updated,
    last_revision_id,
    last_save_callback_at,
    file_size,
    mime_type,
    akte_id,
    customer_id,
    folder_id,
  };
}

/** Cache-Bust fuer OnlyOffice document.url (NC-Share-Links cachen sonst alte Bytes). */
export function bustPortalDocumentDownloadUrl(
  url: string,
  bustToken?: string | number,
): string {
  const raw = url.trim();
  if (!/^https?:\/\//i.test(raw)) return raw;
  if (bustToken == null || bustToken === '') return raw;
  const token = String(bustToken);
  try {
    const u = new URL(raw);
    u.searchParams.set('v', token);
    return u.toString();
  } catch {
    const sep = raw.includes('?') ? '&' : '?';
    return `${raw}${sep}v=${encodeURIComponent(token)}`;
  }
}

function portalLocalAkteScope(akteId?: string | null): string {
  const id = (akteId || '').trim();
  return id || '_';
}

export function portalRecentDocsLegacyStorageKey(customerId: string): string {
  return `arkorbit-portal-recent-docs:${customerId}`;
}

export function portalRecentDocsStorageKey(customerId: string, akteId?: string | null): string {
  const cid = customerId.trim() || 'unknown';
  return `arkorbit-portal-recent-docs:${cid}:${portalLocalAkteScope(akteId)}`;
}

export function portalVirtualFoldersStorageKey(customerId: string): string {
  return `arkorbit-portal-virtual-folders:${customerId}`;
}

export function loadPortalVirtualFolders(customerId: string): string[] {
  try {
    const raw = localStorage.getItem(portalVirtualFoldersStorageKey(customerId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x): x is string => typeof x === 'string' && Boolean(x.trim()))
      .map((x) => sanitizePortalFolderInput(x))
      .filter(Boolean);
  } catch {
    return [];
  }
}

export function savePortalVirtualFolders(customerId: string, folders: string[]): void {
  const unique = Array.from(
    new Set(folders.map((f) => sanitizePortalFolderInput(f)).filter(Boolean)),
  );
  try {
    localStorage.setItem(portalVirtualFoldersStorageKey(customerId), JSON.stringify(unique));
  } catch {
    /* localStorage voll oder blockiert */
  }
}

export const PORTAL_DOC_FAVORITES_MAX = 20;
export const PORTAL_DOC_PINS_MAX = 12;

export type PortalDocShortcutKind = 'document' | 'folder' | 'akte';

export interface PortalDocShortcut {
  id: string;
  kind: PortalDocShortcutKind;
  title: string;
  pb_record_id?: string;
  nextcloud_path?: string;
  document_url?: string;
  akte_id?: string;
}

export function portalDocFavoritesLegacyStorageKey(customerId: string): string {
  return `arkorbit-portal-doc-favorites:${customerId.trim() || 'unknown'}`;
}

export function portalDocFavoritesStorageKey(customerId: string, akteId?: string | null): string {
  const cid = customerId.trim() || 'unknown';
  return `arkorbit-portal-doc-favorites:${cid}:${portalLocalAkteScope(akteId)}`;
}

export function portalDocPinsStorageKey(customerId: string): string {
  return `arkorbit-portal-doc-pins:${customerId.trim() || 'unknown'}`;
}

function sanitizeDocShortcuts(raw: unknown, max: number): PortalDocShortcut[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: PortalDocShortcut[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Partial<PortalDocShortcut>;
    const id = typeof row.id === 'string' ? row.id.trim() : '';
    const title = typeof row.title === 'string' ? row.title.trim() : '';
    const kind = row.kind;
    if (!id || !title || (kind !== 'document' && kind !== 'folder' && kind !== 'akte')) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      kind,
      title,
      pb_record_id: typeof row.pb_record_id === 'string' ? row.pb_record_id : undefined,
      nextcloud_path: typeof row.nextcloud_path === 'string' ? row.nextcloud_path : undefined,
      document_url: typeof row.document_url === 'string' ? row.document_url : undefined,
      akte_id: typeof row.akte_id === 'string' ? row.akte_id : undefined,
    });
    if (out.length >= max) break;
  }
  return out;
}

function loadDocShortcuts(key: string, max: number): PortalDocShortcut[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    return sanitizeDocShortcuts(JSON.parse(raw) as unknown, max);
  } catch {
    return [];
  }
}

function saveDocShortcuts(key: string, items: PortalDocShortcut[], max: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(items.slice(0, max)));
  } catch {
    /* localStorage voll oder blockiert */
  }
}

export function loadPortalDocFavorites(
  customerId: string,
  akteId?: string | null,
): PortalDocShortcut[] {
  const scoped = loadDocShortcuts(
    portalDocFavoritesStorageKey(customerId, akteId),
    PORTAL_DOC_FAVORITES_MAX,
  );
  if (scoped.length > 0 || (akteId || '').trim()) return scoped;
  return loadDocShortcuts(portalDocFavoritesLegacyStorageKey(customerId), PORTAL_DOC_FAVORITES_MAX);
}

export function savePortalDocFavorites(
  customerId: string,
  items: PortalDocShortcut[],
  akteId?: string | null,
): void {
  saveDocShortcuts(portalDocFavoritesStorageKey(customerId, akteId), items, PORTAL_DOC_FAVORITES_MAX);
}

export function togglePortalDocFavorite(
  items: PortalDocShortcut[],
  next: PortalDocShortcut,
): PortalDocShortcut[] {
  const exists = items.some((row) => row.id === next.id);
  if (exists) return items.filter((row) => row.id !== next.id);
  return [next, ...items].slice(0, PORTAL_DOC_FAVORITES_MAX);
}

export function loadPortalDocPins(customerId: string): PortalDocShortcut[] {
  return loadDocShortcuts(portalDocPinsStorageKey(customerId), PORTAL_DOC_PINS_MAX);
}

export function savePortalDocPins(customerId: string, items: PortalDocShortcut[]): void {
  saveDocShortcuts(portalDocPinsStorageKey(customerId), items, PORTAL_DOC_PINS_MAX);
}

export function upsertPortalDocPin(
  items: PortalDocShortcut[],
  next: PortalDocShortcut,
): PortalDocShortcut[] {
  const filtered = items.filter((row) => row.id !== next.id);
  return [next, ...filtered].slice(0, PORTAL_DOC_PINS_MAX);
}

function readRecentPortalDocumentsFromKey(key: string): PortalRecentDocument[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (x): x is PortalRecentDocument =>
          Boolean(x) &&
          typeof x === 'object' &&
          typeof (x as PortalRecentDocument).display_name === 'string' &&
          typeof (x as PortalRecentDocument).document_url === 'string' &&
          typeof (x as PortalRecentDocument).openedAt === 'string',
      )
      .slice(0, PORTAL_RECENT_DOCS_MAX);
  } catch {
    return [];
  }
}

export function loadRecentPortalDocuments(
  customerId: string,
  akteId?: string | null,
): PortalRecentDocument[] {
  const scoped = readRecentPortalDocumentsFromKey(portalRecentDocsStorageKey(customerId, akteId));
  if (scoped.length > 0 || (akteId || '').trim()) return scoped;
  return readRecentPortalDocumentsFromKey(portalRecentDocsLegacyStorageKey(customerId));
}

export function saveRecentPortalDocument(
  customerId: string,
  entry: PortalRecentDocument,
  akteId?: string | null,
): void {
  const existing = loadRecentPortalDocuments(customerId, akteId);
  const dedupeKey = entry.pb_record_id || entry.nextcloud_path || entry.document_url;
  const filtered = existing.filter((e) => {
    const k = e.pb_record_id || e.nextcloud_path || e.document_url;
    return k !== dedupeKey;
  });
  const next = [entry, ...filtered].slice(0, PORTAL_RECENT_DOCS_MAX);
  try {
    localStorage.setItem(portalRecentDocsStorageKey(customerId, akteId), JSON.stringify(next));
  } catch {
    /* localStorage voll oder blockiert */
  }
}

/** Entfernt Browser-only „Zuletzt geöffnet“, wenn PB-Liste leer ist (stale NC-Shares → Extract-502). */
export function clearRecentPortalDocumentsWhenServerEmpty(
  customerId: string,
  serverRows: Pick<PortalProtokoll, 'id'>[],
): boolean {
  if (serverRows.length > 0) return false;
  try {
    const keys = [
      portalRecentDocsStorageKey(customerId),
      portalRecentDocsLegacyStorageKey(customerId),
    ];
    let removed = false;
    for (const key of keys) {
      if (!localStorage.getItem(key)) continue;
      localStorage.removeItem(key);
      removed = true;
    }
    return removed;
  } catch {
    return false;
  }
}

export type PortalDocumentPathGroup = 'portal-documents' | 'contracts-final' | 'other';

export const PORTAL_DOCUMENT_GROUP_ORDER: PortalDocumentPathGroup[] = [
  'portal-documents',
  'contracts-final',
  'other',
];

export const PORTAL_DOCUMENT_GROUP_META: Record<
  PortalDocumentPathGroup,
  { label: string; hint: string }
> = {
  'portal-documents': {
    label: 'Portal-Dokumente',
    hint: 'Uploads und bearbeitete Dateien',
  },
  'contracts-final': {
    label: 'Verträge & Abschlüsse',
    hint: 'Generierte Vertragsdokumente',
  },
  other: {
    label: 'Weitere Dokumente',
    hint: 'Protokolle und sonstige Ablage',
  },
};

export function classifyPortalDocumentGroup(
  nextcloudPath?: string,
  filename?: string,
): PortalDocumentPathGroup {
  const hay = `${nextcloudPath || ''} ${filename || ''}`.toLowerCase();
  if (hay.includes('portal-documents') || hay.includes('portal_documents')) {
    return 'portal-documents';
  }
  if (hay.includes('contracts-final') || hay.includes('contracts_final')) {
    return 'contracts-final';
  }
  return 'other';
}

export function sanitizePortalFolderInput(raw: string): string {
  return raw
    .split(/[\\/]+/)
    .map((p) => p.trim().replace(/[^\w.\- ()äöüÄÖÜß]+/g, '_').replace(/^\.+/, ''))
    .filter((p) => p && p !== '.' && p !== '..')
    .join('/');
}

/** Dateiname ohne Pfadsegmente — gleiche Zeichenklasse wie Ordner-Segmente. */
export function sanitizePortalFileName(raw: string): string {
  const leaf = raw.split(/[\\/]+/).pop()?.trim() || '';
  return leaf.replace(/[^\w.\- ()äöüÄÖÜß]+/g, '_').replace(/^\.+/, '').slice(0, 180);
}

export function applyPortalDocumentRenameToRecent(
  customerId: string,
  args: { pb_record_id: string; display_name: string; nextcloud_path?: string },
  akteId?: string | null,
): void {
  const keys = [
    portalRecentDocsStorageKey(customerId, akteId),
    portalRecentDocsLegacyStorageKey(customerId),
  ];
  for (const key of keys) {
    const existing = readRecentPortalDocumentsFromKey(key);
    if (!existing.length) continue;
    const next = existing.map((entry) => {
      if (entry.pb_record_id !== args.pb_record_id) return entry;
      return {
        ...entry,
        display_name: args.display_name,
        nextcloud_path: args.nextcloud_path ?? entry.nextcloud_path,
      };
    });
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* localStorage voll oder blockiert */
    }
  }
}

/** Pins + Favoriten in localStorage nach Datei-Rename (Chat-Kasten ohne DokumenteView-State). */
export function persistPortalDocumentRenameShortcuts(
  customerId: string,
  args: { pb_record_id: string; title: string; nextcloud_path?: string },
  akteId?: string | null,
): void {
  savePortalDocFavorites(
    customerId,
    applyPortalDocumentRenameToShortcuts(loadPortalDocFavorites(customerId, akteId), args),
    akteId,
  );
  savePortalDocPins(
    customerId,
    applyPortalDocumentRenameToShortcuts(loadPortalDocPins(customerId), args),
  );
}

export function portalFolderApiErrorMessage(
  data: Record<string, unknown>,
  status: number,
): string {
  const err = typeof data.error === 'string' ? data.error.trim() : '';
  const detail = typeof data.detail === 'string' ? data.detail.trim() : '';
  const http = detail.match(/HTTP\s+(\d+)/i);
  if (http?.[1] === '404' || /NotFound/i.test(detail)) {
    return 'Die Datei liegt an diesem Ablageort nicht mehr (HTTP 404).';
  }
  if (err && http?.[1]) return `${err} (HTTP ${http[1]})`;
  if (err) return err;
  if (status === 502) return 'Umbenennen oder Ordner-Aktion in Nextcloud fehlgeschlagen';
  return `HTTP ${status}`;
}

/** Relativer NC-Ordner für Ordner-Upload (webkitRelativePath + aktueller UI-Ordner). */
export function portalUploadFolderPathFromWebkit(
  webkitRelativePath: string,
  baseFolder: string,
): string {
  const parts = webkitRelativePath.split(/[/\\]/).filter(Boolean);
  if (parts.length > 1) parts.pop();
  const subPath = parts.join('/');
  const combined =
    baseFolder.trim() && subPath
      ? `${baseFolder.trim()}/${subPath}`
      : baseFolder.trim() || subPath;
  return sanitizePortalFolderInput(combined);
}

const FOLDER_UPLOAD_SKIP = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);

type PortalFileWithRel = File & { webkitRelativePath?: string };

function portalFileRelativePath(file: File): string {
  return (file as PortalFileWithRel).webkitRelativePath || '';
}

function tagPortalFileRelativePath(file: File, relPath: string): File {
  if (!relPath) return file;
  try {
    Object.defineProperty(file, 'webkitRelativePath', {
      value: relPath,
      configurable: true,
      enumerable: true,
    });
  } catch {
    /* ignore — Browser ohne webkitRelativePath */
  }
  return file;
}

async function readPortalDirectoryEntry(
  entry: FileSystemDirectoryEntry,
  pathPrefix: string,
): Promise<File[]> {
  const reader = entry.createReader();
  const collected: File[] = [];

  const readBatch = (): Promise<void> =>
    new Promise((resolve, reject) => {
      reader.readEntries(
        (entries) => {
          if (!entries.length) {
            resolve();
            return;
          }
          void (async () => {
            for (const child of entries) {
              if (child.isFile) {
                const file = await new Promise<File>((res, rej) => {
                  (child as FileSystemFileEntry).file(res, rej);
                });
                collected.push(tagPortalFileRelativePath(file, `${pathPrefix}${file.name}`));
              } else if (child.isDirectory) {
                const nested = await readPortalDirectoryEntry(
                  child as FileSystemDirectoryEntry,
                  `${pathPrefix}${child.name}/`,
                );
                collected.push(...nested);
              }
            }
            await readBatch();
            resolve();
          })().catch(reject);
        },
        reject,
      );
    });

  await readBatch();
  return collected;
}

/** Drag&Drop: Ordner (mehrere Dateien, webkitRelativePath oder Directory-Entry). */
export function isPortalFolderFileDrop(files: FileList | null | undefined): boolean {
  if (!files?.length) return false;
  if (files.length > 1) return true;
  return /[/\\]/.test(portalFileRelativePath(files[0]));
}

/** Dist-verify Marker — verify-portal-ui-dist.sh (portalDnD); nicht entfernen. */
export const PORTAL_DND_DIST_MARKER = 'portalDnD:v1';

/** DragOver-Hilfe: externe Datei/Ordner (Windows liefert types oft erst nach preventDefault). */
export function portalDragMayAcceptExternalFiles(
  dataTransfer: DataTransfer | null | undefined,
): boolean {
  if (!dataTransfer) return false;
  const types = Array.from(dataTransfer.types || []);
  if (types.some((t) => t === 'Files' || t === 'application/x-moz-file')) return true;
  const items = dataTransfer.items;
  if (items?.length) {
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].kind === 'file') return true;
    }
  }
  return Boolean(dataTransfer.files?.length);
}

/** DataTransfer: Ordner-Drop auch bei einzelnem Directory-Entry (Windows/Chrome). */
export function isPortalFolderDataTransfer(dataTransfer: DataTransfer | null | undefined): boolean {
  if (!dataTransfer) return false;
  if (isPortalFolderFileDrop(dataTransfer.files)) return true;
  const items = dataTransfer.items;
  if (!items?.length) return false;
  for (let i = 0; i < items.length; i += 1) {
    const entry = items[i].webkitGetAsEntry?.();
    if (entry?.isDirectory) return true;
  }
  return false;
}

/**
 * Sammelt alle Dateien aus einem Drop (inkl. Ordner-Rekursion via webkitGetAsEntry).
 */
export async function collectPortalDropFiles(
  dataTransfer: DataTransfer | null | undefined,
): Promise<File[]> {
  if (!dataTransfer) return [];

  const fromItems: File[] = [];
  const items = dataTransfer.items;
  if (items?.length) {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      const entry = item.webkitGetAsEntry?.();
      if (!entry) {
        const file = item.getAsFile();
        if (file) fromItems.push(file);
        continue;
      }
      if (entry.isFile) {
        const file = await new Promise<File>((resolve, reject) => {
          (entry as FileSystemFileEntry).file(resolve, reject);
        });
        fromItems.push(file);
      } else if (entry.isDirectory) {
        const nested = await readPortalDirectoryEntry(
          entry as FileSystemDirectoryEntry,
          `${entry.name}/`,
        );
        fromItems.push(...nested);
      }
    }
  }

  if (fromItems.length) return fromItems;

  const list = dataTransfer.files;
  if (!list?.length) return [];
  // Marker im Bundle halten (DnD-Smoke / verify-portal-ui-dist.sh)
  if (PORTAL_DND_DIST_MARKER.length < 1) return [];
  return Array.from(list);
}

/** Dateien aus Ordner-Upload filtern (keine System-/Hidden-Dateien). */
export function filterPortalFolderUploadFiles(files: FileList | File[]): File[] {
  const list = Array.from(files);
  return list.filter((file) => {
    const name = file.name.trim();
    if (!name || name.startsWith('.')) return false;
    if (FOLDER_UPLOAD_SKIP.has(name.toLowerCase())) return false;
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || name;
    if (rel.split(/[/\\]/).some((seg) => seg.startsWith('.'))) return false;
    return true;
  });
}

export interface PortalDocumentFolderNode {
  name: string;
  path: string;
  children: PortalDocumentFolderNode[];
  documents: PortalProtokoll[];
}

/** Ordnerbaum aus PB-Zeilen (nur Pfade unter portal-documents). */
export function buildPortalDocumentFolderTree(
  docs: PortalProtokoll[],
  currentFolder = '',
): PortalDocumentFolderNode {
  const root: PortalDocumentFolderNode = { name: '', path: '', children: [], documents: [] };
  const folderMap = new Map<string, PortalDocumentFolderNode>();
  folderMap.set('', root);

  const ensureFolder = (relPath: string, segment: string): PortalDocumentFolderNode => {
    const existing = folderMap.get(relPath);
    if (existing) return existing;
    const parentPath = relPath.includes('/')
      ? relPath.slice(0, relPath.lastIndexOf('/'))
      : '';
    const parent = ensureFolder(parentPath, parentPath.split('/').pop() || '');
    const node: PortalDocumentFolderNode = { name: segment, path: relPath, children: [], documents: [] };
    folderMap.set(relPath, node);
    parent.children.push(node);
    parent.children.sort((a, b) => a.name.localeCompare(b.name, 'de'));
    return node;
  };

  for (const doc of docs) {
    const rel = portalDocumentRelativeFolder(doc.nextcloud_path);
    if (!rel) {
      root.documents.push(doc);
      continue;
    }
    const segments = rel.split('/').filter(Boolean);
    let acc = '';
    for (const seg of segments) {
      acc = acc ? `${acc}/${seg}` : seg;
      ensureFolder(acc, seg);
    }
    const leaf = folderMap.get(rel);
    if (leaf) leaf.documents.push(doc);
    else root.documents.push(doc);
  }

  if (currentFolder) {
    return folderMap.get(currentFolder) || root;
  }
  return root;
}

export function portalFolderDisplayPath(folderPath: string): string {
  if (!folderPath.trim()) return 'Alle Dokumente';
  return folderPath.split('/').join(' / ');
}

/** Unmittelbare Unterordner von `currentFolder` — Windows-Explorer, kein flacher Gesamtbaum. */
export function portalExplorerChildFolders(
  allFolders: string[],
  currentFolder = '',
): string[] {
  const cwd = sanitizePortalFolderInput(currentFolder);
  const prefix = cwd ? `${cwd}/` : '';
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of allFolders) {
    const folder = sanitizePortalFolderInput(raw);
    if (!folder || folder === cwd) continue;
    if (cwd && !folder.startsWith(prefix)) continue;
    const rest = cwd ? folder.slice(prefix.length) : folder;
    const first = rest.split('/').filter(Boolean)[0];
    if (!first) continue;
    const childPath = cwd ? `${cwd}/${first}` : first;
    if (seen.has(childPath)) continue;
    seen.add(childPath);
    out.push(childPath);
  }
  return out.sort((a, b) => a.localeCompare(b, 'de'));
}

export function portalExplorerParentFolder(folderPath: string, rootPath = ''): string {
  const cwd = sanitizePortalFolderInput(folderPath);
  const root = sanitizePortalFolderInput(rootPath);
  if (!cwd || cwd === root) return root;
  const parent = cwd.includes('/') ? cwd.slice(0, cwd.lastIndexOf('/')) : '';
  if (root && parent !== root && !parent.startsWith(`${root}/`)) return root;
  return parent;
}

export function portalExplorerDocInCurrentFolder(docFolder: string, currentFolder: string): boolean {
  return sanitizePortalFolderInput(docFolder) === sanitizePortalFolderInput(currentFolder);
}

export function portalExplorerBreadcrumb(
  currentFolder: string,
  opts?: { rootPath?: string; rootLabel?: string },
): { label: string; path: string }[] {
  const cwd = sanitizePortalFolderInput(currentFolder);
  const rootPath = sanitizePortalFolderInput(opts?.rootPath || '');
  const rootLabel = opts?.rootLabel?.trim() || 'Ablage';
  const crumbs: { label: string; path: string }[] = [{ label: rootLabel, path: rootPath }];
  if (!cwd || cwd === rootPath) return crumbs;
  const rest =
    rootPath && cwd.startsWith(`${rootPath}/`) ? cwd.slice(rootPath.length + 1) : cwd;
  let acc = rootPath;
  for (const part of rest.split('/').filter(Boolean)) {
    acc = acc ? `${acc}/${part}` : part;
    crumbs.push({ label: part, path: acc });
  }
  return crumbs;
}

/** n8n-Ingest → Hub-Zeile (PB-Id oder temporäre Zeile bis Index nachzieht). */
export function protokollFromIngestFields(fields: PortalDocumentOpenFields): PortalProtokoll | null {
  const pbId = fields.pb_record_id?.trim();
  const docUrl = fields.document_url?.trim();
  if (!docUrl) return null;
  const pathKey = fields.nextcloud_path?.trim() || docUrl;
  return {
    id: pbId || `pending:${pathKey}`,
    filename: fields.display_name?.trim() || 'Dokument',
    nc_url: docUrl,
    nextcloud_path: fields.nextcloud_path?.trim() || undefined,
    created: new Date().toISOString(),
    updated: fields.updated,
    akte_id: fields.akte_id?.trim() || undefined,
  };
}

export function isPortalPdfDocument(filename: string, nextcloudPath?: string, documentUrl?: string): boolean {
  const ext =
    fileExtensionFromName(filename) ||
    fileExtensionFromName(nextcloudPath || '') ||
    fileExtensionFromName(documentUrl || '');
  return ext === 'pdf';
}

export function mergeProtokollIntoList(
  list: PortalProtokoll[],
  item: PortalProtokoll,
): PortalProtokoll[] {
  const dedupeKey = item.id || item.nextcloud_path || item.nc_url;
  const filtered = list.filter((p) => {
    const k = p.id || p.nextcloud_path || p.nc_url;
    if (k === dedupeKey) return false;
    if (item.nextcloud_path && p.nextcloud_path === item.nextcloud_path) return false;
    if (item.nc_url && p.nc_url === item.nc_url) return false;
    return true;
  });
  return [item, ...filtered];
}

export function resolveProtokollDocumentUrl(
  protokolle: PortalProtokoll[],
  fields: { pb_record_id?: string; document_url: string },
): string {
  if (fields.pb_record_id) {
    const match = protokolle.find((p) => p.id === fields.pb_record_id);
    if (match?.nc_url) return match.nc_url;
  }
  return fields.document_url;
}

/** Frische Download-URL aus PB (z. B. wenn Hub-Liste noch leer oder Share-Link veraltet). */
export async function fetchPortalProtokollById(
  pbBaseUrl: string,
  collection: string,
  recordId: string,
  authToken: string,
): Promise<PortalProtokoll | null> {
  const base = pbBaseUrl.replace(/\/$/, '');
  const id = recordId.trim();
  const token = authToken.trim();
  if (!base || !id || !token) return null;
  try {
    const res = await fetch(`${base}/api/collections/${collection}/records/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const rec = (await res.json()) as Record<string, unknown>;
    if (!isActivePortalDocumentRecord(rec)) return null;
    const row = mapPbRecordToProtokoll(rec);
    return row.id ? row : null;
  } catch {
    return null;
  }
}

export function formatDeAtDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface PortalDocumentOpenFields {
  display_name: string;
  document_url: string;
  pb_record_id?: string;
  nextcloud_path?: string;
  updated?: string;
  last_revision_id?: number;
  last_save_callback_at?: string;
  /** PB-Relation — nur echte Id, nie aus dem NC-Pfad geraten. */
  akte_id?: string;
  /** n8n: PB-Index fehlgeschlagen, NC-Upload aber ok */
  pb_warning?: string;
}

/** n8n PORTAL_DOCUMENT_UPLOAD / NC-Import → Felder für Editor-Öffnung. */
export function parsePortalDocumentN8nResponse(
  data: Record<string, unknown>,
  res: Response,
): PortalDocumentOpenFields {
  const docUrl = typeof data.document_url === 'string' ? data.document_url.trim() : '';
  const payloadOk = data.ok === true || data.ok === 'true';
  if (!res.ok || (!payloadOk && !docUrl)) {
    throw new Error(formatPortalUploadError(res, data, 'upload'));
  }
  if (!docUrl) {
    throw new Error('Die Datei wurde gespeichert, aber die Server-Antwort fehlt. Bitte erneut versuchen.');
  }
  const dn = typeof data.display_name === 'string' ? data.display_name : 'Dokument';
  const pbUploadId =
    (typeof data.pb_record_id === 'string' && data.pb_record_id.trim()) ||
    (typeof data.pb_id === 'string' && data.pb_id.trim()) ||
    (typeof data.record_id === 'string' && data.record_id.trim()) ||
    undefined;
  const nextcloud_path =
    typeof data.nextcloud_path === 'string' && data.nextcloud_path.trim()
      ? data.nextcloud_path.trim()
      : undefined;
  const pbWarning =
    typeof data.pb_warning === 'string' && data.pb_warning.trim() ? data.pb_warning.trim() : undefined;
  const akte_id =
    (typeof data.akte_id === 'string' && data.akte_id.trim()) ||
    (typeof data.akteId === 'string' && data.akteId.trim()) ||
    undefined;
  return {
    display_name: dn,
    document_url: docUrl,
    pb_record_id: pbUploadId,
    nextcloud_path,
    akte_id,
    pb_warning: pbWarning,
  };
}

export function portalDocumentOpenFieldsFromProtokoll(doc: PortalProtokoll): PortalDocumentOpenFields {
  return {
    display_name: doc.filename,
    document_url: doc.nc_url,
    pb_record_id: doc.id,
    nextcloud_path: doc.nextcloud_path,
    updated: doc.updated,
    last_revision_id: doc.last_revision_id,
    last_save_callback_at: doc.last_save_callback_at,
    akte_id: doc.akte_id,
  };
}

export function pbRecordIdFromPortalDocumentId(documentId: string): string | null {
  const id = documentId.trim();
  if (!id.startsWith('pb:')) return null;
  const pb = id.slice(3).trim();
  return pb || null;
}

export function portalUploadOpenAfterSaveStorageKey(customerId: string): string {
  return `arkorbit_portal_upload_open_after_save_v1__${customerId.trim() || 'unknown'}`;
}

/** Erstbesuch: aus. Danach merkt localStorage den Haken „Sofort öffnen“. */
export function loadPortalUploadOpenAfterSave(customerId: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(portalUploadOpenAfterSaveStorageKey(customerId));
    if (raw === null) return false;
    return raw === '1';
  } catch {
    return false;
  }
}

export function savePortalUploadOpenAfterSave(customerId: string, openAfterSave: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      portalUploadOpenAfterSaveStorageKey(customerId),
      openAfterSave ? '1' : '0',
    );
  } catch {
    /* localStorage blockiert oder voll — Haken bleibt nur für diese Session. */
  }
}

export async function renamePortalDocumentViaFolderApi(args: {
  customerId: string;
  customerStatus?: string;
  customerEmail?: string;
  pbRecordId: string;
  sourcePath: string;
  newName: string;
}): Promise<{ display_name: string; nextcloud_path?: string }> {
  if (!PORTAL_DOCUMENT_FOLDER_URL) {
    throw new Error('Umbenennen ist in diesem Build nicht konfiguriert.');
  }
  if (!portalAuthzSidecarExpected()) {
    throw new Error('Ordner-API: Auf diesem Portal noch nicht freigeschaltet (AuthZ).');
  }
  if (!hasPortalAuthzToken()) {
    throw new Error('Bitte neu anmelden (kein gültiges Portal-Token).');
  }
  const url = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_FOLDER_URL);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...portalAuthzHeaders(url),
    },
    credentials: 'omit',
    body: JSON.stringify({
      action: 'rename',
      folderPath: '',
      customerId: args.customerId,
      permissions: 19,
      ...getPortalTenantN8nExtras(args.customerStatus, args.customerEmail),
      pb_record_id: args.pbRecordId,
      sourcePath: args.sourcePath,
      newName: args.newName,
    }),
  });
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    /* ignore */
  }
  if (!res.ok || data.ok === false) {
    throw new Error(portalFolderApiErrorMessage(data, res.status));
  }
  const pbWarning =
    typeof data.pb_warning === 'string' && data.pb_warning.trim() ? data.pb_warning.trim() : '';
  if (pbWarning) {
    throw new Error(`Umbenennen: Dateiindex nicht aktualisiert (${pbWarning})`);
  }
  const display_name =
    typeof data.display_name === 'string' && data.display_name.trim()
      ? data.display_name.trim()
      : args.newName;
  const nextcloud_path =
    typeof data.nextcloud_path === 'string' && data.nextcloud_path.trim()
      ? data.nextcloud_path.trim()
      : undefined;
  return { display_name, nextcloud_path };
}

export async function movePortalDocumentViaFolderApi(args: {
  customerId: string;
  customerStatus?: string;
  customerEmail?: string;
  pbRecordId: string;
  sourcePath: string;
  targetFolderPath: string;
}): Promise<{ nextcloud_path: string; akte_id?: string }> {
  if (!PORTAL_DOCUMENT_FOLDER_URL) {
    throw new Error('Verschieben ist in diesem Build nicht konfiguriert.');
  }
  if (!portalAuthzSidecarExpected()) {
    throw new Error('Ordner-API: Auf diesem Portal noch nicht freigeschaltet (AuthZ).');
  }
  if (!hasPortalAuthzToken()) {
    throw new Error('Bitte neu anmelden (kein gültiges Portal-Token).');
  }
  const url = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_FOLDER_URL);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...portalAuthzHeaders(url),
    },
    credentials: 'omit',
    body: JSON.stringify({
      action: 'move',
      folderPath: args.targetFolderPath,
      customerId: args.customerId,
      permissions: 19,
      ...getPortalTenantN8nExtras(args.customerStatus, args.customerEmail),
      pb_record_id: args.pbRecordId,
      sourcePath: args.sourcePath,
      targetFolderPath: args.targetFolderPath,
    }),
  });
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    /* ignore */
  }
  if (!res.ok || data.ok === false) {
    throw new Error(portalFolderApiErrorMessage(data, res.status));
  }
  const pbWarning =
    typeof data.pb_warning === 'string' && data.pb_warning.trim() ? data.pb_warning.trim() : '';
  if (pbWarning) {
    throw new Error(`Verschieben: Dateiindex nicht aktualisiert (${pbWarning})`);
  }
  const nextcloud_path =
    typeof data.nextcloud_path === 'string' && data.nextcloud_path.trim()
      ? data.nextcloud_path.trim()
      : '';
  if (!nextcloud_path) {
    throw new Error('Verschieben konnte nicht über PocketBase bestätigt werden.');
  }
  const akte_id =
    typeof data.akte_id === 'string' && data.akte_id.trim() ? data.akte_id.trim() : undefined;
  const expectedAkteId =
    args.targetFolderPath.replace(/\\/g, '/').match(/(?:^|\/)akten\/([a-zA-Z0-9]{10,20})(?:\/|$)/)?.[1];
  if (expectedAkteId && akte_id !== expectedAkteId) {
    throw new Error('Verschieben konnte der Ziel-Akte nicht bestätigt werden.');
  }
  return { nextcloud_path, akte_id };
}

export async function archivePortalDocumentRecord(
  recordId: string,
): Promise<{ ok: true } | { ok: false; status?: number; error: string }> {
  const id = recordId.trim();
  const token = getPortalPbToken();
  if (!id) return { ok: false, error: 'Dokument-Id fehlt.' };
  if (!token) return { ok: false, error: 'Bitte neu anmelden.' };
  const res = await fetch(
    `${getPortalPocketBaseUrl().replace(/\/$/, '')}/api/collections/${PORTAL_PROTOKOLLE_COLLECTION}/records/${id}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status: 'archived' }),
    },
  );
  if (res.ok) return { ok: true };
  if (res.status === 403) {
    return {
      ok: false,
      status: 403,
      error: 'Dieses Dokument gehört zu einer anderen Akte und lässt sich hier nicht entfernen.',
    };
  }
  return { ok: false, status: res.status, error: 'Dokument konnte nicht aus der Liste entfernt werden.' };
}

export async function patchPortalDocumentAkteId(args: {
  recordId: string;
  akteId: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const recordId = args.recordId.trim();
  const akteId = args.akteId.trim();
  const token = getPortalPbToken();
  if (!recordId || !akteId) return { ok: false, error: 'Dokument und Akte sind Pflicht.' };
  if (!token) return { ok: false, error: 'Bitte neu anmelden.' };
  const res = await fetch(
    `${getPortalPocketBaseUrl().replace(/\/$/, '')}/api/collections/${PORTAL_PROTOKOLLE_COLLECTION}/records/${recordId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ akte_id: akteId }),
    },
  );
  if (res.ok) return { ok: true };
  return { ok: false, error: 'Akte konnte nicht zugeordnet werden.' };
}

/** NC-Ordner anlegen (Drop-Fix / Schreiben in gebundene Akte). Existierender Ordner ist ok. */
export async function createPortalFolderViaApi(args: {
  customerId: string;
  folderPath: string;
  customerStatus?: string;
  customerEmail?: string;
}): Promise<void> {
  const folderPath = args.folderPath.trim();
  if (!folderPath) return;
  if (!PORTAL_DOCUMENT_FOLDER_URL) {
    throw new Error('Ordner-API ist in diesem Build nicht konfiguriert.');
  }
  if (!portalAuthzSidecarExpected()) {
    throw new Error('Ordner-API: Auf diesem Portal noch nicht freigeschaltet (AuthZ).');
  }
  if (!hasPortalAuthzToken()) {
    throw new Error('Bitte neu anmelden (kein gültiges Portal-Token).');
  }
  const url = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_FOLDER_URL);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...portalAuthzHeaders(url),
    },
    credentials: 'omit',
    body: JSON.stringify({
      action: 'create',
      folderPath,
      customerId: args.customerId,
      permissions: 19,
      ...getPortalTenantN8nExtras(args.customerStatus, args.customerEmail),
    }),
  });
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    /* ignore */
  }
  if (!res.ok || data.ok === false) {
    throw new Error(portalFolderApiErrorMessage(data, res.status));
  }
}
